import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Plus, Search, MoreVertical, Edit, Key, Power, Trash2,
  Users as UsersIcon, CheckCircle, XCircle, Shield, Eye, FileCheck,
  AlertTriangle, X as XIcon
} from 'lucide-react';
import { Card, Button, Badge, Modal } from '../components/ui';
import { UserFormModal } from '../components/users/UserFormModal';
import { DeleteUserModal } from '../components/users/DeleteUserModal';
import { ViewUserModal } from '../components/users/ViewUserModal';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { usersApi } from '../services/usersApi';
import './UsersPage.css';

const getInitials = (firstName, lastName) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const createTemporaryPassword = () => `AgriLink!${Math.random().toString(36).slice(2, 10)}9`;

const getAvatarColor = (userId) => {
  // Use primary color to match layout
  return '#4A5A3A';
};

const getRoleBadgeConfig = (role) => {
  const configs = {
    superadmin: { icon: Shield, color: '#8A4A3A', bg: 'rgba(138, 74, 58, 0.12)', border: 'rgba(138, 74, 58, 0.2)' },
    admin: { icon: Shield, color: '#3A4A2A', bg: 'rgba(74, 90, 58, 0.12)', border: 'rgba(74, 90, 58, 0.2)' },
    reviewer: { icon: FileCheck, color: '#6A5A3A', bg: 'rgba(138, 122, 74, 0.12)', border: 'rgba(138, 122, 74, 0.2)' },
    viewer: { icon: Eye, color: '#4A4A4A', bg: 'rgba(107, 107, 107, 0.12)', border: 'rgba(107, 107, 107, 0.2)' },
    custom: { icon: Shield, color: '#7C3AED', bg: 'rgba(124, 58, 237, 0.12)', border: 'rgba(124, 58, 237, 0.2)' },
  };
  return configs[role] || configs.viewer;
};

export function UsersPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    adminUsers: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Set role filter from location state (when navigating from dashboard)
  useEffect(() => {
    if (location.state?.roleFilter) {
      setRoleFilter(location.state.roleFilter);
    }
  }, [location.state]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkResetModal, setShowBulkResetModal] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkDeleteConfirmed, setBulkDeleteConfirmed] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Load stats + users list (server-side filters)
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const params = {
          page: 1,
          limit: 200,
          role: roleFilter !== 'all' ? roleFilter : undefined,
          isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
          search: searchQuery ? searchQuery.trim() : undefined,
          sortBy: 'email',
          sortOrder: 'asc',
        };

        const [statsRes, listRes] = await Promise.all([
          usersApi.getStats(),
          usersApi.list(params),
        ]);

        const items = Array.isArray(listRes?.items) ? listRes.items : [];
        if (!cancelled) {
          setUsers(items);
          setStats({
            totalUsers: statsRes?.totalUsers ?? items.length,
            activeUsers: statsRes?.activeUsers ?? items.filter(u => u.isActive).length,
            adminUsers: statsRes?.adminUsers ?? items.filter(u => u.role === 'admin' || u.role === 'superadmin').length,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setUsers([]);
          setStats({ totalUsers: 0, activeUsers: 0, adminUsers: 0 });
          setLoadError(e?.message || t('errors.generic'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [roleFilter, statusFilter, searchQuery, refreshToken, t]);

  // Filter users
  const filteredUsers = useMemo(() => {
    return users;
  }, [users, searchQuery, roleFilter, statusFilter]);

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedUsers(prev => 
      prev.length === filteredUsers.length ? [] : filteredUsers.map(u => u.id)
    );
  };

  const handleDeleteUser = (user) => {
    if (currentUser?.email && user.email === currentUser.email) {
      toast({
        title: t('common.error'),
        description: t('users.toasts.cannotDeleteCurrentUser'),
        variant: 'error',
      });
      setOpenMenuId(null);
      return;
    }

    setUserToDelete(user);
    setShowDeleteModal(true);
    setOpenMenuId(null);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setOpenMenuId(null);
  };

  const handleViewUser = (user) => {
    setViewingUser(user);
    setOpenMenuId(null);
  };

  const handleResetPassword = async (user) => {
    setOpenMenuId(null);

    try {
      await usersApi.resetPassword(user.id, createTemporaryPassword());
      toast({
        title: t('common.success'),
        description: t('users.toasts.passwordResetSent', { email: user.email }),
        variant: 'success',
      });
      setRefreshToken(x => x + 1);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error?.message || t('users.toasts.bulkPasswordResetFailed'),
        variant: 'error',
      });
    }
  };

  const handleToggleUserStatus = async (user) => {
    setOpenMenuId(null);

    if (currentUser?.email && user.email === currentUser.email && user.isActive) {
      toast({
        title: t('common.error'),
        description: t('users.toasts.cannotDeactivateCurrentUser'),
        variant: 'error',
      });
      return;
    }

    try {
      await usersApi.update(user.id, {
        ...user,
        isActive: !user.isActive,
      });
      toast({
        title: t('common.success'),
        description: user.isActive
          ? t('users.toasts.userDeactivated', { name: `${user.firstName} ${user.lastName}` })
          : t('users.toasts.userActivated', { name: `${user.firstName} ${user.lastName}` }),
        variant: 'success',
      });
      setRefreshToken(x => x + 1);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error?.message || t('errors.generic'),
        variant: 'error',
      });
    }
  };

  const confirmDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setSelectedUsers([]);
    toast({
      title: t('common.success'),
      description: t('users.toasts.userDeleted'),
      variant: 'success',
    });
    setRefreshToken(x => x + 1);
  };
  
  const handleCreateSuccess = () => {
    setSelectedUsers([]);
    toast({
      title: t('common.success'),
      description: t('users.toasts.userCreated'),
      variant: 'success',
    });
    setRefreshToken(x => x + 1);
  };
  
  const handleUpdateSuccess = () => {
    setEditingUser(null);
    setSelectedUsers([]);
    toast({
      title: t('common.success'),
      description: t('users.toasts.userUpdated'),
      variant: 'success',
    });
    setRefreshToken(x => x + 1);
  };

  // Get selected user objects
  const selectedUserObjects = useMemo(() => {
    return users.filter(u => selectedUsers.includes(u.id));
  }, [users, selectedUsers]);

  // Check if current user is in selection
  const isCurrentUserSelected = useMemo(() => {
    if (!currentUser) return false;
    // Match by email since currentUser might have different ID structure
    return selectedUserObjects.some(u => u.email === currentUser.email);
  }, [selectedUserObjects, currentUser]);

  // Handle bulk reset password
  const handleBulkResetPassword = async () => {
    setBulkActionLoading(true);
    try {
      await Promise.all(selectedUserObjects.map((user) => usersApi.resetPassword(user.id, createTemporaryPassword())));
      
      toast({
        title: t('common.success'),
        description: t('users.toasts.bulkPasswordResetSent', { count: selectedUsers.length }),
        variant: 'success',
      });
      
      setShowBulkResetModal(false);
      setSelectedUsers([]);
      setRefreshToken(x => x + 1);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error?.message || t('users.toasts.bulkPasswordResetFailed'),
        variant: 'error',
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    // Filter out current user from deletion
    const usersToDelete = selectedUserObjects.filter(u => {
      if (!currentUser) return true;
      return u.email !== currentUser.email;
    });

    if (usersToDelete.length === 0) {
      toast({
        title: t('common.error'),
        description: t('users.bulk.cannotDeleteSelf'),
        variant: 'error',
      });
      return;
    }

    setBulkActionLoading(true);
    try {
      await Promise.all(usersToDelete.map((user) => usersApi.delete(user.id)));
      
      const deletedCount = usersToDelete.length;
      const skippedCount = selectedUsers.length - deletedCount;
      
      toast({
        title: t('common.success'),
        description: skippedCount > 0
          ? t('users.toasts.bulkUsersDeletedWithSkipped', { deleted: deletedCount, skipped: skippedCount })
          : t('users.toasts.bulkUsersDeleted', { deleted: deletedCount }),
        variant: 'success',
      });
      
      setShowBulkDeleteModal(false);
      setBulkDeleteConfirmed(false);
      setSelectedUsers([]);
      setRefreshToken(x => x + 1);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error?.message || t('users.toasts.bulkUsersDeleteFailed'),
        variant: 'error',
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  return (
    <div className="users-page">

      {/* Statistics Cards */}
      <div className="users-stats">
        <Card className="stat-card" padding="md">
          <div className="stat-icon-container stat-icon-container--primary">
            <UsersIcon size={24} />
          </div>
          <div className="stat-label">{t('users.stats.totalUsers')}</div>
          <div className="stat-value">{stats.totalUsers}</div>
        </Card>

        <Card className="stat-card" padding="md">
          <div className="stat-icon-container stat-icon-container--success">
            <CheckCircle size={24} />
          </div>
          <div className="stat-label">{t('users.stats.activeUsers')}</div>
          <div className="stat-value">{stats.activeUsers}</div>
        </Card>

        <Card className="stat-card" padding="md">
          <div className="stat-icon-container stat-icon-container--primary">
            <Shield size={24} />
          </div>
          <div className="stat-label">{t('users.stats.adminUsers')}</div>
          <div className="stat-value">{stats.adminUsers}</div>
        </Card>
      </div>

      {loadError && (
        <div className="users-empty-state" style={{ marginBottom: '12px' }}>
          <UsersIcon size={48} />
          <h3 className="empty-state-title">{t('common.error')}</h3>
          <p className="empty-state-message">{loadError}</p>
        </div>
      )}

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="filters-bar__left">
          <select
            className="filter-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">{t('users.filters.role.all')}</option>
            <option value="admin">{t('users.filters.role.admin')}</option>
            <option value="reviewer">{t('users.filters.role.reviewer')}</option>
            <option value="viewer">{t('users.filters.role.viewer')}</option>
            <option value="custom">{t('users.filters.role.custom')}</option>
          </select>

          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t('users.filters.status.all')}</option>
            <option value="active">{t('users.filters.status.active')}</option>
            <option value="inactive">{t('users.filters.status.inactive')}</option>
          </select>
        </div>
        
        <div className="filters-bar__right">
          <div className="search-input-wrapper">
            <Search className="search-input-icon" size={20} />
            <input
              type="text"
              className="search-input"
              placeholder={t('users.header.search.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant="primary"
            icon={<Plus size={18} />}
            onClick={() => setShowAddModal(true)}
          >
            {t('users.header.addUser')}
          </Button>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedUsers.length > 0 && (
        <div className="bulk-actions-toolbar">
          <div className="bulk-actions-info">
            <span className="bulk-actions-count">
              {selectedUsers.length === 1
                ? t('users.bulk.selectedOne')
                : t('users.bulk.selectedMany', { count: selectedUsers.length })}
            </span>
            {isCurrentUserSelected && (
              <span className="bulk-actions-warning">
              <AlertTriangle size={14} />
              {t('users.bulk.cannotDeleteSelf')}
            </span>
            )}
          </div>
          <div className="bulk-actions-buttons">
            <Button
              variant="secondary"
              icon={<Key size={16} />}
              onClick={() => setShowBulkResetModal(true)}
            >
              {t('users.bulk.actions.resetPasswords')}
            </Button>
            <Button
              variant="danger"
              icon={<Trash2 size={16} />}
              onClick={() => setShowBulkDeleteModal(true)}
            >
              {t('users.bulk.actions.deleteUsers')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setSelectedUsers([])}
            >
              {t('users.bulk.actions.clearSelection')}
            </Button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="users-table-container">
        <div className="users-table-header">
          <div>
            <input
              type="checkbox"
              className="table-checkbox"
              checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
              onChange={toggleSelectAll}
            />
          </div>
          <div>{t('users.table.columns.user')}</div>
          <div>{t('users.table.columns.role')}</div>
          <div>{t('users.table.columns.status')}</div>
          <div>{t('users.table.columns.actions')}</div>
        </div>

        {filteredUsers.map((user) => {
          const roleConfig = getRoleBadgeConfig(user.role);
          const RoleIcon = roleConfig.icon;

          return (
            <div key={user.id} className="users-table-row">
              <div>
                <input
                  type="checkbox"
                  className="table-checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => toggleUserSelection(user.id)}
                />
              </div>

              <div className="user-cell">
                <div
                  className="user-avatar"
                  style={{ background: getAvatarColor(user.id) }}
                >
                  {getInitials(user.firstName, user.lastName)}
                </div>
                <div className="user-details">
                  <div className="user-name">{user.firstName} {user.lastName}</div>
                  <div className="user-email">{user.email}</div>
                </div>
              </div>

              <div className="role-cell">
                <span
                  className="role-badge"
                  style={{
                    background: roleConfig.bg,
                    color: roleConfig.color,
                    border: `1px solid ${roleConfig.border}`,
                  }}
                >
                  <RoleIcon size={14} />
                  {t(`users.table.roles.${user.role}`)}
                </span>
              </div>

              <div className="status-cell">
                {user.isActive ? (
                  <span className="status-badge status-badge--active">
                    <CheckCircle size={12} />
                    {t('users.table.status.active')}
                  </span>
                ) : (
                  <span className="status-badge status-badge--inactive">
                    <XCircle size={12} />
                    {t('users.table.status.inactive')}
                  </span>
                )}
              </div>

              <div className="actions-cell">
                <div className="actions-menu-wrapper">
                  <button
                    className="action-menu-button"
                    onClick={() => setOpenMenuId(openMenuId === user.id ? null : user.id)}
                  >
                    <MoreVertical size={18} />
                  </button>

                  {openMenuId === user.id && (
                    <div className="action-menu-dropdown">
                      <button 
                        className="action-menu-item"
                        onClick={() => handleViewUser(user)}
                      >
                        <Eye size={16} />
                        {t('users.table.actions.viewDetails')}
                      </button>
                      <button 
                        className="action-menu-item"
                        onClick={() => handleEditUser(user)}
                      >
                        <Edit size={16} />
                        {t('users.table.actions.edit')}
                      </button>
                      <button
                        className="action-menu-item"
                        onClick={() => handleResetPassword(user)}
                      >
                        <Key size={16} />
                        {t('users.table.actions.resetPassword')}
                      </button>
                      <button
                        className="action-menu-item"
                        onClick={() => handleToggleUserStatus(user)}
                      >
                        <Power size={16} />
                        {user.isActive ? t('users.table.actions.deactivate') : t('users.table.actions.activate')}
                      </button>
                      <div className="action-menu-divider" />
                      <button
                        className="action-menu-item action-menu-item--danger"
                        onClick={() => handleDeleteUser(user)}
                      >
                        <Trash2 size={16} />
                        {t('users.table.actions.delete')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {!loading && filteredUsers.length === 0 && (
          <div className="users-empty-state">
            <UsersIcon size={64} />
            <h3 className="empty-state-title">{t('users.empty.title')}</h3>
            <p className="empty-state-message">{t('users.empty.message')}</p>
            {(searchQuery || roleFilter !== 'all' || statusFilter !== 'all') && (
              <Button
                variant="secondary"
                onClick={() => {
                  setSearchQuery('');
                  setRoleFilter('all');
                  setStatusFilter('all');
                }}
              >
                {t('users.empty.clearFilters')}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredUsers.length > 0 && (
        <div className="users-pagination">
          <div className="pagination-info">
            {t('users.pagination.showing')} 1-{filteredUsers.length} {t('users.pagination.of')} {filteredUsers.length} {t('users.pagination.results')}
          </div>
        </div>
      )}

      {/* Role Permissions Explanation */}
      <div className="role-permissions-section">
        <h3 className="role-permissions-title">{t('users.permissions.title')}</h3>
        <div className="role-permissions-grid">
          <div className="role-permission-card">
            <div className="role-permission-header">
              <Shield size={20} />
              <span className="role-permission-name">{t('users.table.roles.admin')}</span>
            </div>
            <div className="role-permission-description">
              {t('users.permissions.roles.admin.description')}
            </div>
            <div className="role-permission-pages">
              <span className="permission-badge">{t('users.permissions.allPages')}</span>
            </div>
          </div>

          <div className="role-permission-card">
            <div className="role-permission-header">
              <FileCheck size={20} />
              <span className="role-permission-name">{t('users.table.roles.reviewer')}</span>
            </div>
            <div className="role-permission-description">
              {t('users.permissions.roles.reviewer.description')}
            </div>
            <div className="role-permission-pages">
              <span className="permission-badge">{t('nav.dashboard')}</span>
              <span className="permission-badge">{t('nav.analytics')}</span>
              <span className="permission-badge">{t('nav.map')}</span>
              <span className="permission-badge">{t('nav.stations')}</span>
              <span className="permission-badge">{t('nav.reports')}</span>
            </div>
          </div>

          <div className="role-permission-card">
            <div className="role-permission-header">
              <Eye size={20} />
              <span className="role-permission-name">{t('users.table.roles.viewer')}</span>
            </div>
            <div className="role-permission-description">
              {t('users.permissions.roles.viewer.description')}
            </div>
            <div className="role-permission-pages">
              <span className="permission-badge">{t('nav.dashboard')}</span>
              <span className="permission-badge">{t('nav.map')}</span>
              <span className="permission-badge">{t('nav.stations')}</span>
            </div>
          </div>

          <div className="role-permission-card">
            <div className="role-permission-header">
              <Shield size={20} />
              <span className="role-permission-name">{t('users.table.roles.custom')}</span>
            </div>
            <div className="role-permission-description">
              {t('users.permissions.roles.custom.description')}
            </div>
            <div className="role-permission-pages">
              <span className="permission-badge">{t('users.permissions.configurable')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit User Modal */}
      <UserFormModal
        isOpen={showAddModal || !!editingUser}
        onClose={() => {
          setShowAddModal(false);
          setEditingUser(null);
        }}
        onSuccess={editingUser ? handleUpdateSuccess : handleCreateSuccess}
        user={editingUser}
      />

      {/* Delete User Modal */}
      <DeleteUserModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onSuccess={confirmDelete}
        user={userToDelete || {}}
      />

      {/* View User Modal */}
      <ViewUserModal
        isOpen={!!viewingUser}
        onClose={() => setViewingUser(null)}
        user={viewingUser}
      />

      {/* Bulk Reset Password Modal */}
      <Modal isOpen={showBulkResetModal} onClose={() => setShowBulkResetModal(false)} size="sm">
        <div className="bulk-action-modal">
          <div className="bulk-modal-header">
            <Key size={32} className="bulk-modal-icon" />
            <h2 className="bulk-modal-title">{t('users.bulk.resetPasswordsModal.title')}</h2>
          </div>
          <div className="bulk-modal-body">
            <p>
              {t('users.bulk.resetPasswordsModal.message', { count: selectedUsers.length })}
            </p>
            <p className="bulk-modal-note">
              {t('users.bulk.resetPasswordsModal.note')}
            </p>
          </div>
          <div className="modal-footer">
            <Button
              variant="secondary"
              onClick={() => setShowBulkResetModal(false)}
              disabled={bulkActionLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleBulkResetPassword}
              disabled={bulkActionLoading}
            >
              {bulkActionLoading ? t('users.bulk.resetPasswordsModal.sending') : t('users.bulk.resetPasswordsModal.confirm')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Delete Modal */}
      <Modal 
        isOpen={showBulkDeleteModal} 
        onClose={() => {
          setShowBulkDeleteModal(false);
          setBulkDeleteConfirmed(false);
        }} 
        size="sm"
      >
        <div className="bulk-action-modal">
          <div className="bulk-modal-header">
            <AlertTriangle size={32} className="bulk-modal-icon bulk-modal-icon--warning" />
            <h2 className="bulk-modal-title">{t('users.bulk.deleteUsersModal.title')}</h2>
          </div>
          <div className="bulk-modal-body">
            <p>
              {t('users.bulk.deleteUsersModal.message', { count: selectedUsers.length })}
            </p>
            {isCurrentUserSelected && (
              <div className="bulk-modal-warning-box">
                <AlertTriangle size={16} />
                <span>{t('users.bulk.deleteUsersModal.cannotDeleteSelfNote')}</span>
              </div>
            )}
            <p className="bulk-modal-warning">
              {t('users.bulk.deleteUsersModal.warning')}
            </p>
            <div className="confirm-checkbox">
              <label className="form-checkbox-label">
                <input
                  type="checkbox"
                  checked={bulkDeleteConfirmed}
                  onChange={(e) => setBulkDeleteConfirmed(e.target.checked)}
                  disabled={bulkActionLoading}
                />
                <span>{t('users.bulk.deleteUsersModal.confirmCheckbox')}</span>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <Button
              variant="secondary"
              onClick={() => {
                setShowBulkDeleteModal(false);
                setBulkDeleteConfirmed(false);
              }}
              disabled={bulkActionLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (!bulkDeleteConfirmed) {
                  toast({
                    title: t('common.error'),
                    description: t('users.bulk.deleteUsersModal.confirmRequired'),
                    variant: 'error',
                  });
                  return;
                }
                handleBulkDelete();
              }}
              disabled={!bulkDeleteConfirmed || bulkActionLoading}
            >
              {bulkActionLoading ? t('users.bulk.deleteUsersModal.deleting') : t('users.bulk.deleteUsersModal.confirm')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
